import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, ArrowLeft } from 'lucide-react';
import { BusinessSettings } from '../components/settings/BusinessSettings';
import { BookingWidgetSettings } from '../components/settings/BookingWidgetSettings';
import { ReminderSettings } from '../components/settings/ReminderSettings';
import { NotificationSettings } from '../components/settings/NotificationSettings';
import { AccountSettings } from '../components/settings/AccountSettings';

export default function Settings() {
  const navigate = useNavigate();
  const [briefingMode, setBriefingMode] = useState('auto');
  const [businessProfile, setBusinessProfile] = useState({
    business_name: '',
    photographer_name: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    state_of_operation: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBriefingMode(data.briefing_mode);
        if (data.business_profile) {
          setBusinessProfile(prev => ({ ...prev, ...data.business_profile }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBriefingModeChange = async (mode) => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ briefing_mode: mode })
      });
      if (response.ok) {
        setBriefingMode(mode);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBusinessProfileChange = (field, value) => {
    setBusinessProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveBusinessProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ business_profile: businessProfile })
      });
      if (response.ok) {
        alert('Business profile saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save business profile:', error);
      alert('Failed to save business profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 pt-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-surface rounded-lg transition-colors"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </button>
            <SettingsIcon className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
          </div>
          <p className="text-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 pt-4 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
            aria-label="Back to dashboard"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-text-muted" />
          </button>
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        </div>

        <BusinessSettings
          profile={businessProfile}
          onChange={handleBusinessProfileChange}
          onSave={saveBusinessProfile}
          saving={saving}
        />

        <BookingWidgetSettings />

        <ReminderSettings />

        <NotificationSettings
          briefingMode={briefingMode}
          onChange={handleBriefingModeChange}
          saving={saving}
        />

        <AccountSettings />
      </div>
    </div>
  );
}
